<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { getCadastroConfig } from '@/config/cadastros'
import { isCampoNumerico } from '@/config/validation'
import CampoEditavel from './CampoEditavel.vue'
import type { ItemFalha, ItemCorrecao, TipoCadastro } from '@/types'

interface Props {
  itens: ItemFalha[]
  tipo: TipoCadastro
  loading?: boolean
}

interface Emits {
  (e: 'reprocessar', itens: ItemCorrecao[]): void
  (e: 'descartar'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// INVALIDO/ERRO são corrigíveis; DUPLICADO é apenas exibido (decisão de produto)
const corrigiveis = computed(() => props.itens.filter(i => i.status === 'INVALIDO' || i.status === 'ERRO'))
const duplicados = computed(() => props.itens.filter(i => i.status === 'DUPLICADO'))

// Campo que dá nome ao registro, por tipo
const CAMPO_NOME: Record<TipoCadastro, string> = {
  clientes: 'cliente',
  fornecedores: 'fornecedor',
  credores: 'credor',
  produtos: 'produto',
  ativos: 'ativo_descricao'
}
const rotuloNome = computed(() => CAMPO_NOME[props.tipo])

// Cópia editável dos dados por cod (tudo string para casar com os inputs)
const edicoes = ref<Record<number, Record<string, string>>>({})

const sincronizar = () => {
  const novo: Record<number, Record<string, string>> = {}
  corrigiveis.value.forEach(item => {
    const linha: Record<string, string> = {}
    Object.entries(item.dados).forEach(([k, v]) => {
      linha[k] = v === null || v === undefined ? '' : String(v)
    })
    novo[item.cod] = linha
  })
  edicoes.value = novo
}

watch(() => props.itens, sincronizar, { immediate: true })

// Campos com erro específico de um item (dedup, ignora 'geral')
const camposComErroDoItem = (item: ItemFalha): string[] => {
  const vistos = new Set<string>()
  item.erros.forEach(e => {
    if (e.campo && e.campo !== 'geral') vistos.add(e.campo)
  })
  return [...vistos]
}

// Colunas editáveis = união dos campos com erro entre todos os registros.
// Se algum item tem só erro 'geral'/inserção (sem campo), inclui os obrigatórios.
const colunasComErro = computed(() => {
  const set = new Set<string>()
  let temGeral = false
  corrigiveis.value.forEach(item => {
    const campos = camposComErroDoItem(item)
    if (campos.length === 0) temGeral = true
    campos.forEach(c => set.add(c))
  })
  if (temGeral) {
    getCadastroConfig(props.tipo).camposObrigatorios.forEach(c => set.add(c))
  }
  return [...set]
})

// Lookup: cod → (campo → mensagem) para destaque/tooltip
const errosPorCodCampo = computed(() => {
  const mapa = new Map<number, Map<string, string>>()
  corrigiveis.value.forEach(item => {
    const cm = new Map<string, string>()
    item.erros.forEach(e => {
      if (e.campo && e.campo !== 'geral' && !cm.has(e.campo)) cm.set(e.campo, e.mensagem)
    })
    mapa.set(item.cod, cm)
  })
  return mapa
})
const temErro = (cod: number, campo: string): boolean => errosPorCodCampo.value.get(cod)?.has(campo) ?? false
const mensagemErro = (cod: number, campo: string): string | undefined => errosPorCodCampo.value.get(cod)?.get(campo)

const resumoErros = (item: ItemFalha): string => {
  if (item.erros.length > 0) {
    return item.erros.map(e => (e.campo && e.campo !== 'geral' ? `${e.campo}: ${e.mensagem}` : e.mensagem)).join(' · ')
  }
  return item.motivo || 'Erro ao processar'
}

// Cabeçalhos do grid (estilo planilha)
const headers = computed(() => [
  { title: 'Linha', key: 'linha', width: 58, sortable: true },
  { title: 'Status', key: 'status', width: 82, sortable: true },
  { title: rotuloNome.value, key: '__nome', width: 150, sortable: true },
  ...colunasComErro.value.map(campo => ({ title: campo, key: campo, width: 140, sortable: false })),
  { title: 'Problemas', key: '__problemas', width: 220, sortable: false }
])

// Linhas do grid (metadados estáveis; valores editáveis vêm de `edicoes` via slot)
const linhasTabela = computed(() =>
  corrigiveis.value.map(item => ({
    cod: item.cod,
    linha: item.linha,
    status: item.status,
    __nome: (item.dados[CAMPO_NOME[props.tipo]] != null ? String(item.dados[CAMPO_NOME[props.tipo]]) : '').trim() || '—',
    __problemas: resumoErros(item)
  }))
)

// Correção em massa: campos que falham em 2+ registros corrigíveis
const camposEmMassa = computed(() => {
  const contagem: Record<string, number> = {}
  corrigiveis.value.forEach(item => {
    new Set(camposComErroDoItem(item)).forEach(campo => {
      contagem[campo] = (contagem[campo] || 0) + 1
    })
  })
  return Object.entries(contagem)
    .filter(([, n]) => n >= 2)
    .map(([campo, quantidade]) => ({ campo, quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade)
})

// Lookup campo → quantidade, para o controle de massa no cabeçalho da coluna
const massaPorCampo = computed<Record<string, number>>(() => {
  const m: Record<string, number> = {}
  camposEmMassa.value.forEach(({ campo, quantidade }) => {
    m[campo] = quantidade
  })
  return m
})

const valoresMassa = ref<Record<string, string>>({})

// Uma célula é candidata ao preenchimento em massa quando está em branco
const cellVazia = (cod: number, campo: string): boolean =>
  ((edicoes.value[cod]?.[campo]) ?? '').trim() === ''

// Quantos registros estão com a célula em branco na coluna (serão preenchidos pelo lote),
// independentemente de terem um erro registrado naquele campo específico
const afetadosEmMassa = (campo: string): number =>
  corrigiveis.value.filter(item => cellVazia(item.cod, campo)).length

// Estado do modal de confirmação (null = fechado). Captura campo/valor/qtd no momento do clique.
const confirmacao = ref<{ campo: string; valor: string; quantidade: number } | null>(null)

const dialogAberto = computed({
  get: () => confirmacao.value !== null,
  set: (aberto: boolean) => { if (!aberto) confirmacao.value = null }
})

// Abre o modal de confirmação do preenchimento em massa
const solicitarAplicarEmMassa = (campo: string) => {
  confirmacao.value = {
    campo,
    valor: valoresMassa.value[campo] ?? '',
    quantidade: afetadosEmMassa(campo)
  }
}

// Preenche o valor em TODA célula em branco da coluna (tenha erro registrado ou não).
// Não sobrescreve células que já têm valor.
const aplicarEmMassa = (campo: string, valor: string) => {
  // Defesa: campos numéricos não recebem valores negativos no preenchimento em lote
  const valorFinal = isCampoNumerico(campo) ? valor.replace(/-/g, '') : valor
  corrigiveis.value.forEach(item => {
    if (cellVazia(item.cod, campo) && edicoes.value[item.cod]) {
      edicoes.value[item.cod][campo] = valorFinal
    }
  })
}

const confirmarAplicarEmMassa = () => {
  if (confirmacao.value) {
    aplicarEmMassa(confirmacao.value.campo, confirmacao.value.valor)
  }
  confirmacao.value = null
}

const reprocessar = () => {
  const itens: ItemCorrecao[] = corrigiveis.value.map(item => ({
    cod: item.cod,
    dados: edicoes.value[item.cod] ?? {}
  }))
  emit('reprocessar', itens)
}
</script>

<template>
  <v-card v-if="corrigiveis.length > 0 || duplicados.length > 0" class="mb-4" variant="outlined">
    <v-card-title class="text-subtitle-1 font-weight-medium">
      <v-icon start size="small" color="warning">mdi-pencil-box-multiple</v-icon>
      Corrigir registros pendentes
    </v-card-title>

    <v-card-text>
      <v-alert
        v-if="corrigiveis.length > 0"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ corrigiveis.length }} registro(s) não foram inseridos. Edite as células destacadas e clique em
        <strong>Reprocessar corrigidos</strong>. Para corrigir vários de uma vez, use o campo no topo da coluna.
      </v-alert>

      <!-- Grid de correção (estilo planilha) -->
      <v-data-table-virtual
        v-if="corrigiveis.length > 0"
        :headers="headers"
        :items="linhasTabela"
        item-value="cod"
        height="460"
        fixed-header
        density="compact"
        class="grid-correcao border rounded"
      >
        <!-- Cabeçalho das colunas editáveis: nome do campo + preenchimento em massa -->
        <template
          v-for="campo in colunasComErro"
          :key="'h-' + campo"
          #[`header.${campo}`]
        >
          <div class="py-1">
            <div class="font-weight-medium mb-1">{{ campo }}</div>
            <div v-if="massaPorCampo[campo]" class="d-flex align-center ga-1">
              <CampoEditavel
                v-model="valoresMassa[campo]"
                :campo="campo"
                denso
                class="header-massa"
              />
              <v-btn
                size="x-small"
                icon="mdi-arrow-down-bold"
                color="primary"
                variant="tonal"
                title="Preencher em massa (apenas células vazias)"
                @click="solicitarAplicarEmMassa(campo)"
              />
            </div>
          </div>
        </template>

        <template #item.status="{ item }">
          <v-chip size="x-small" :color="item.status === 'INVALIDO' ? 'error' : 'deep-orange'" label>
            {{ item.status }}
          </v-chip>
        </template>

        <template
          v-for="campo in colunasComErro"
          :key="campo"
          #[`item.${campo}`]="{ item }"
        >
          <CampoEditavel
            v-if="edicoes[item.cod]"
            v-model="edicoes[item.cod][campo]"
            :campo="campo"
            :error="temErro(item.cod, campo)"
            :hint="mensagemErro(item.cod, campo)"
            denso
            class="cell-input"
          />
        </template>

        <template #item.__problemas="{ item }">
          <span class="text-caption text-error">{{ item.__problemas }}</span>
        </template>
      </v-data-table-virtual>

      <!-- Duplicados (somente leitura) -->
      <template v-if="duplicados.length > 0">
        <div class="text-subtitle-2 font-weight-medium mt-4 mb-2">
          <v-icon size="small" color="warning" class="mr-1">mdi-content-copy</v-icon>
          Duplicados ({{ duplicados.length }}) — não são reenviados
        </div>
        <v-list density="compact" class="rounded border" max-height="200" style="overflow-y: auto;">
          <v-list-item v-for="item in duplicados" :key="'dup-' + item.cod">
            <v-list-item-title class="text-body-2">
              Linha {{ item.linha }}: {{ item.motivo || 'Registro duplicado' }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </template>

      <div class="d-flex justify-end ga-2 mt-4">
        <v-btn
          variant="text"
          color="grey-darken-1"
          prepend-icon="mdi-close"
          :disabled="loading"
          @click="emit('descartar')"
        >
          Descartar
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          prepend-icon="mdi-reload"
          :loading="loading"
          :disabled="corrigiveis.length === 0"
          @click="reprocessar"
        >
          Reprocessar corrigidos
        </v-btn>
      </div>
    </v-card-text>

    <!-- Confirmação do preenchimento em massa -->
    <v-dialog v-model="dialogAberto" max-width="440">
      <v-card v-if="confirmacao">
        <v-card-title class="text-subtitle-1 font-weight-medium d-flex align-center">
          <v-icon start size="small" color="primary">mdi-arrow-down-bold-box</v-icon>
          Preencher em massa
        </v-card-title>
        <v-card-text>
          <template v-if="confirmacao.valor.trim() === ''">
            <v-alert type="warning" variant="tonal" density="compact">
              Digite um valor no campo do cabeçalho da coluna
              <strong>{{ confirmacao.campo }}</strong> antes de aplicar em massa.
            </v-alert>
          </template>
          <template v-else-if="confirmacao.quantidade === 0">
            <v-alert type="info" variant="tonal" density="compact">
              Não há células vazias em <strong>{{ confirmacao.campo }}</strong> para preencher.
              Nada será alterado.
            </v-alert>
          </template>
          <template v-else>
            <p class="mb-3">
              Preencher o campo <strong>{{ confirmacao.campo }}</strong> com o valor
              <strong>"{{ confirmacao.valor }}"</strong> em
              <strong>{{ confirmacao.quantidade }}</strong> registro(s)?
            </p>
            <v-alert type="info" variant="tonal" density="compact">
              Apenas as células vazias serão preenchidas. Registros já corrigidos não serão alterados.
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmacao = null">Cancelar</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :disabled="confirmacao.quantidade === 0 || confirmacao.valor.trim() === ''"
            @click="confirmarAplicarEmMassa"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style scoped>
.grid-correcao :deep(td),
.grid-correcao :deep(th) {
  white-space: nowrap;
  font-size: 0.78rem;
}
/* Compacta a altura das linhas com inputs */
.grid-correcao :deep(td) {
  padding-top: 2px;
  padding-bottom: 2px;
}
/* Cabeçalho com controle de massa cresce em altura; alinha ao topo */
.grid-correcao :deep(th) {
  vertical-align: top;
  height: auto;
}

/* Inputs menores: fonte e altura reduzidas */
.grid-correcao :deep(.v-field) {
  font-size: 0.75rem;
}
.grid-correcao :deep(.v-field__input) {
  font-size: 0.75rem;
  min-height: 28px;
  padding-top: 2px;
  padding-bottom: 2px;
}
.grid-correcao :deep(.v-field__append-inner),
.grid-correcao :deep(.v-field__clearable) {
  padding-top: 2px;
}
.grid-correcao :deep(.v-field__append-inner .v-icon) {
  font-size: 16px;
}

.cell-input {
  min-width: 118px;
}
.header-massa {
  min-width: 96px;
  max-width: 132px;
}
</style>
